import { useCallback, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useValueVariable from './useValueVariable'

function ValueVariable({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation(['bootcamp', 'generics'])
  const {
    equationString,
    variable,
    variableValue,
    formValid,
    inputRef,
    answer,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useValueVariable({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
    answerState,
    autofocus,
  })

  useEmitInstance(
    onInstanceChange,
    equationString ? `${variable} = ${variableValue}. ${equationString.replace(/<[^>]+>/g, '')} = \\square` : null,
  )

  const [key, setKey] = useState<string>(``)

  const handleGoToNextQuestion = useCallback((): void => {
    goToNextQuestion()
    setKey(uuid())
  }, [goToNextQuestion, setKey])

  const onEnterPress = useCallback((): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }, [answerState, formValid, handleCheckAnswer, handleGoToNextQuestion])

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex-col">
        <span>
          {t('bootcamp:valueOf', {
            variable,
            value: variableValue,
          })}
        </span>
        <div className="flex flex-row items-center mt-2">
          <span className="tracking-widest" dangerouslySetInnerHTML={{ __html: equationString }} />
          <span className="block sm:inline-block">
            <Equals />
            <Input
              aria-label={t('generics:ariaLabelButtons.insertValue')}
              autoFocus={autofocus}
              isError={answerState === false}
              key={key}
              maxDigits={5}
              onChange={(value) => onInputChange(value)}
              onEnter={onEnterPress}
              ref={inputRef}
              value={answer}
            />
          </span>
        </div>
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(''),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default ValueVariable
